import React from "react";

import { Avatar, CardHeader, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const getInitials = name =>
	String(name || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part[0])
		.join("")
		.toUpperCase();

const useStyles = makeStyles(theme => ({
	root: {
		width: "100%",
		minWidth: 0,
		padding: theme.spacing(1, 0.5),
		overflow: "hidden",
		"& .MuiCardHeader-content": {
			minWidth: 0,
			overflow: "hidden",
		},
		"& .MuiCardHeader-avatar": {
			flexShrink: 0,
			marginRight: theme.spacing(1.25),
			[theme.breakpoints.down("xs")]: {
				marginRight: theme.spacing(0.75),
			},
		},
		"& .MuiAvatar-root": {
			[theme.breakpoints.down("xs")]: {
				width: 40,
				height: 40,
			},
		},
	},
	titleRow: {
		display: "flex",
		alignItems: "center",
		minWidth: 0,
	},
	titleText: {
		display: "block",
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	subheader: {
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		maxWidth: "100%",
		minWidth: 0,
	},
	avatarStack: {
		display: "inline-flex",
		alignItems: "center",
		minWidth: 0,
		overflow: "hidden",
		paddingLeft: 2,
	},
	assignedAvatar: {
		width: 22,
		height: 22,
		fontSize: "0.68rem",
		border: `2px solid ${theme.palette.background.paper}`,
		marginLeft: -4,
		"&:first-child": {
			marginLeft: 0,
		},
	},
	assignedAvatarOnline: {
		boxShadow: "0 0 0 2px #2e7d32",
	},
	assignedAvatarOffline: {
		boxShadow: "0 0 0 2px #d32f2f",
	},
	avatarOverflow: {
		minWidth: 22,
		height: 22,
		padding: "0 5px",
		borderRadius: "50%",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		marginLeft: -4,
		fontSize: "0.65rem",
		fontWeight: 600,
		backgroundColor: theme.palette.action.selected,
		color: theme.palette.text.secondary,
		border: `2px solid ${theme.palette.background.paper}`,
		flexShrink: 0,
	},
}));

const TicketInfo = ({ contact, ticket, onClick }) => {
	const classes = useStyles();
	const title = contact?.name || contact?.number || "";
	const assignedUsers = [
		ticket?.user,
		...(Array.isArray(ticket?.participants)
			? ticket.participants.map(participant => participant.user || participant).filter(Boolean)
			: []),
	].filter(Boolean);
	const uniqueAssignedUsers = assignedUsers.filter(
		(assignedUser, index, self) =>
			assignedUser?.id &&
			self.findIndex(item => Number(item?.id) === Number(assignedUser.id)) === index
	);
	const visibleAssignedUsers = uniqueAssignedUsers.slice(0, 3);
	const hiddenAssignedUsers = Math.max(0, uniqueAssignedUsers.length - visibleAssignedUsers.length);

	return (
		<CardHeader
			onClick={onClick}
			className={classes.root}
			style={{ cursor: "pointer" }}
			titleTypographyProps={{ component: "div", color: "textPrimary" }}
			subheaderTypographyProps={{ component: "div", color: "textSecondary" }}
			avatar={
				<Avatar src={contact.profilePicUrl} alt="contact_image">
					{getInitials(contact.name)}
				</Avatar>
			}
			title={
				<div className={classes.titleRow}>
					<Tooltip arrow title={title}>
						<span className={classes.titleText}>{title}</span>
					</Tooltip>
				</div>
			}
			subheader={
				uniqueAssignedUsers.length > 0 && (
					<span className={classes.subheader}>
						<span className={classes.avatarStack}>
							{visibleAssignedUsers.map(assignedUser => (
								<Tooltip key={assignedUser.id} arrow title={assignedUser.name || ""}>
									<Avatar
										src={assignedUser.profilePicUrl}
										className={[
											classes.assignedAvatar,
											typeof assignedUser.isOnline === "boolean"
												? assignedUser.isOnline
													? classes.assignedAvatarOnline
													: classes.assignedAvatarOffline
												: "",
										].filter(Boolean).join(" ")}
									>
										{getInitials(assignedUser.name)}
									</Avatar>
								</Tooltip>
							))}
							{hiddenAssignedUsers > 0 && (
								<Tooltip arrow title={uniqueAssignedUsers.slice(3).map(item => item.name).join(", ")}>
									<span className={classes.avatarOverflow}>+{hiddenAssignedUsers}</span>
								</Tooltip>
							)}
						</span>
					</span>
				)
			}
		/>
	);
};

export default TicketInfo;
